import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import SkipButtonBase from 'src/components/AzarOnly/Login/SkipButton';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import Button from 'src/components/Button';
import EthnicityGuideModal from 'src/components/EthnicityGuideModal';
import { ModalContent, ModalFooter as ModalFooterBase, ModalHeader as ModalHeaderBase, ModalLoading, ModalTitle, ModalTitleContainer as ModalTitleContainerBase, ModalTitleDescription as ModalTitleDescriptionBase } from 'src/components/ModalLayout';
import ResizingText from 'src/components/ResizingText';
import { showModalAtom } from 'src/stores/modal/atoms';
import { loginModalHeaderAtom } from 'src/stores/phoneNumberLogin/atoms';
import { ethnicityAtom, EthnicitySelection, ETHNICITY_SKIP_OPTION, FALLBACK_SIGNUP_SETTINGS, signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import { hoverAndActiveCss } from 'src/utils/styled';

/**
 * 스킵 버튼으로 스킵한 경우에는 돌아왔을 때 선택 상태를 유지하지 않음
 */
const excludeSkipButtonSelection = (selection: EthnicitySelection | null): EthnicitySelection | null => selection?.source === 'skipButton' ? null : selection;
const ModalHeader = styled(ModalHeaderBase)`
  flex-direction: row;
  justify-content: flex-end;
`;
const DesktopSkipButton = styled(SkipButtonBase)`
  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const MobileSkipButton = styled(SkipButtonBase)`
  display: none;

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: block;
    width: fit-content;
    padding: 0;
  }
`;
const ModalTitleContainer = styled(ModalTitleContainerBase)`
  ${({
  theme
}) => theme.screenSize.mobile} {
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;
const DescriptionText = styled.span``;
const LineBreaker = styled.br``;
const GuideModalOpenButton = styled.button`
  all: unset;
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
  font-weight: 700;
`;
const ModalTitleDescription = styled(ModalTitleDescriptionBase)`
  font-size: 13px;
  line-height: 18px;
  white-space: pre-line;

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 14px;
    line-height: 20px;
  }
`;
const EthnicityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0px;
`;
const EthnicityOption = styled.button<{
  shouldBeDeHighlighted: boolean;
}>`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  height: 56px;
  padding-inline-end: 16px;
  padding-inline-start: 20px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  background-color: ${({
  theme
}) => theme.color.surface4};

  color: ${({
  theme,
  shouldBeDeHighlighted
}) => shouldBeDeHighlighted ? theme.color.content6 : theme.color.content12};

  ${({
  theme
}) => hoverAndActiveCss(`
      background-color: ${theme.color.surface5};
    `)}
`;
const EthnicityOptionText = styled(ResizingText)`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
const EthnicityStep = () => {
  const [ethnicityInRegisterForm, setEthnicityInRegisterForm] = useAtom(ethnicityAtom);
  const [selectedEthnicity, setSelectedEthnicity] = useState<EthnicitySelection | null>(excludeSkipButtonSelection(ethnicityInRegisterForm));
  const showModal = useSetAtom(showModalAtom);
  const {
    t
  } = useTranslation();
  const {
    tryRegister
  } = useRegister();
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const setLoginModalHeader = useSetAtom(loginModalHeaderAtom);
  const signupSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const handleClickSkip = useCallback(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_SKIP_ETHNICITY,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'ethnicity',
        target: 'skip'
      }
    });
    setEthnicityInRegisterForm({
      key: ETHNICITY_SKIP_OPTION,
      source: 'skipButton'
    });
    tryRegister();
  }, [tryRegister, setEthnicityInRegisterForm]);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__IMPRESSION_ETHNICITY,
      eventParams: {
        action_category: 'impression',
        tab: 'signup',
        page: 'ethnicity',
        target: 'target'
      }
    });
  }, []);
  useEffect(() => {
    setLoginModalHeader({
      component: <ModalHeader>
          {signupSettings.ethnicitySettings?.skipEnabled && <MobileSkipButton onSkip={handleClickSkip} />}
        </ModalHeader>
    });
    return () => {
      setLoginModalHeader(undefined);
    };
  }, [handleClickSkip, setLoginModalHeader, signupSettings.ethnicitySettings?.skipEnabled]);
  useEffect(() => {
    setSelectedEthnicity(excludeSkipButtonSelection(ethnicityInRegisterForm));
  }, [ethnicityInRegisterForm]);
  useEffect(() => {
    if (signUpSettingsLoadable.state === 'loading') return;
    if (!signupSettings.ethnicitySettings) {
      if (signUpSettingsLoadable.state === 'hasData') {
        Sentry.captureMessage('Entered EthnicityStep without ethnicitySettings');
      }
      tryRegister();
    }
  }, [tryRegister, signUpSettingsLoadable.state, signupSettings.ethnicitySettings]);
  if (signUpSettingsLoadable.state === 'loading') {
    return <ModalLoading />;
  }
  if (!signupSettings.ethnicitySettings) {
    return null;
  }
  const hasSelectedEthnicity = selectedEthnicity !== null;
  const handleClickLearnMore = () => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_LEARN_MORE_ETHNICITY,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'ethnicity',
        target: 'learn_more'
      }
    });
    showModal({
      key: ModalType.ETHNICITY_GUIDE,
      component: () => <EthnicityGuideModal />
    });
  };
  const handleClickNext = () => {
    if (!hasSelectedEthnicity) return;
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_NEXT_ETHNICITY,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'ethnicity',
        target: 'next',
        result: selectedEthnicity.key
      }
    });
    setEthnicityInRegisterForm(selectedEthnicity);
    tryRegister();
  };
  return <>
      <ModalTitleContainer data-sentry-element="ModalTitleContainer" data-sentry-source-file="index.tsx">
        <ModalTitle data-testid='LoginModal-EthnicityStep' data-sentry-element="ModalTitle" data-sentry-source-file="index.tsx">
          {t('ONBOARDING__ETHNICITY_TITLE')}
        </ModalTitle>
        <ModalTitleDescription data-sentry-element="ModalTitleDescription" data-sentry-source-file="index.tsx">
          <DescriptionText data-sentry-element="DescriptionText" data-sentry-source-file="index.tsx">{t('ONBOARDING__ETHNICITY_PROFILE')}</DescriptionText>
          <LineBreaker data-sentry-element="LineBreaker" data-sentry-source-file="index.tsx" />
          <DescriptionText data-sentry-element="DescriptionText" data-sentry-source-file="index.tsx">{t('ONBOARDING__ETHNICITY_WHY')}</DescriptionText>{' '}
          <GuideModalOpenButton onClick={handleClickLearnMore} data-testid='EthnicityStep-LearnMore' data-sentry-element="GuideModalOpenButton" data-sentry-source-file="index.tsx">
            {t('face_verification_body_link')}
          </GuideModalOpenButton>
        </ModalTitleDescription>
      </ModalTitleContainer>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <EthnicityList data-sentry-element="EthnicityList" data-sentry-source-file="index.tsx">
          {signupSettings.ethnicitySettings.ethnicities.map(({
          key,
          displayName
        }) => {
          const isActive = selectedEthnicity?.key === key;
          return <EthnicityOption key={key} onClick={() => setSelectedEthnicity({
            key,
            source: 'option'
          })} shouldBeDeHighlighted={hasSelectedEthnicity && !isActive} data-testid='EthnicityStep-ethnicity-option' data-isactive={isActive}>
                <EthnicityOptionText maxLines={1} minFontSize={14}>
                  {displayName}
                </EthnicityOptionText>
              </EthnicityOption>;
        })}
        </EthnicityList>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button id='sign-up-ethnicity-next' onClick={handleClickNext} disabled={!hasSelectedEthnicity} data-testid='EthnicityStep-button-next' data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('SIGNUP__NEXT')}
        </Button>
        {signupSettings.ethnicitySettings.skipEnabled && <DesktopSkipButton onSkip={handleClickSkip} data-testid='EthnicityStep-button-skip' />}
      </ModalFooter>
    </>;
};
export default EthnicityStep;